import { Link } from "react-router-dom";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";
import { UserPlus, Settings, TrendingUp, AlertTriangle, Gavel, CheckCircle, ArrowRight, Download, Mail, Phone } from "lucide-react";

/* ── Fasi del rapporto di lavoro ─────────────────────────────────────── */
const FASI = [
  {
    num: "01",
    icon: UserPlus,
    titolo: "Assunzione",
    items: [
      "Analisi delle esigenze aziendali e individuazione del contratto più adeguato e più vantaggioso",
      "Redazione e revisione di contratti di lavoro nel rispetto della normativa vigente",
      "Informativa e compliance su obblighi aziendali, inclusa la gestione della privacy e delle comunicazioni obbligatorie",
    ],
  },
  {
    num: "02",
    icon: Settings,
    titolo: "Gestione",
    items: [
      "Applicazione dei contratti collettivi di riferimento e adeguamenti normativi",
      "Consulenza su orario di lavoro, ferie, permessi e welfare aziendale",
      "Implementazione di strumenti per la gestione delle performance e valutazione del personale",
      "Supporto nella gestione delle relazioni sindacali e dei rapporti con gli enti ispettivi",
    ],
  },
  {
    num: "03",
    icon: TrendingUp,
    titolo: "Sviluppo",
    items: [
      "Piani di carriera e crescita professionale",
      "Formazione e aggiornamento delle competenze dei dipendenti",
      "Implementazione di politiche di welfare aziendale e work-life balance",
      "Supporto nei processi di riorganizzazione e innovazione aziendale",
    ],
  },
  {
    num: "04",
    icon: AlertTriangle,
    titolo: "Rapporto in crisi",
    items: [
      "Strumenti di gestione delle crisi aziendali: ammortizzatori sociali, contratti di solidarietà, CIG",
      "Assistenza nella gestione di esuberi e procedure di riduzione del personale",
      "Piani di ricollocazione e outplacement",
      "Supporto nella gestione di licenziamenti individuali e collettivi nel rispetto della normativa vigente",
    ],
  },
  {
    num: "05",
    icon: Gavel,
    titolo: "Contenzioso stragiudiziale",
    items: [
      "Gestione di contestazioni disciplinari e provvedimenti sanzionatori",
      "Mediazione e risoluzione stragiudiziale delle controversie di lavoro",
      "Assistenza nelle conciliazioni sindacali e presso enti di certificazione del lavoro",
      "Redazione di accordi transattivi per la risoluzione delle dispute in via extragiudiziale",
    ],
  },
];

/* ── Aree di competenza ─────────────────────────────────────────────── */
const SERVIZI = [
  {
    id: "consulenza-hr",
    label: "Consulenza HR",
    heading: "Offriamo consulenza in diritto del lavoro, organizzazione e diritti sindacali.",
    body: "Assistiamo le aziende nell'affrontare e gestire i rapporti di lavoro con i dipendenti; nell'applicare i CCNL di categoria fornendo l'interpretazione autentica dei contratti collettivi sottoscritti da Confcommercio (Terziario, Distribuzione e Servizi, Turismo, Logistica e Trasporti, Sport, ETC); nel migliorare il clima interno e costruire una buona talent retention in azienda.",
    items: [
      "Interpretazione e applicazione dei CCNL",
      "Assunzioni agevolate",
      "Modifica mansioni",
      "Best practice e scelte organizzative certificate",
      "Employer Branding",
    ],
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&q=80&auto=format",
    imageAlt: "Consulenza HR — firma contratto",
    imageRight: true,
  },
  {
    id: "assistenza",
    legacyId: "rapporti-lavoro",
    label: "Assistenza",
    heading: "Assistiamo le aziende nella risoluzione stragiudiziale delle controversie di lavoro nell'ambito di Commissioni di Conciliazione, Certificazione e Arbitrato.",
    body: "Siamo al fianco delle aziende nell'analisi dei modelli organizzativi e gestione delle riorganizzazioni aziendali; presidiamo i tavoli sindacali in qualità di controparte datoriale ai sensi dalla normativa di legge con particolare riguardo a:",
    items: [
      "Licenziamenti individuali, plurimi e collettivi",
      "Ammortizzatori sociali",
      "Cessioni e trasferimenti di azienda",
      "Procedure Disciplinari",
    ],
    afterItems: "Offriamo consulenza nella redazione e definizione dei Regolamenti di policy interna e dei contratti collettivi aziendali; valutazione degli «spazi» normativi per la realizzazione di un contratto integrativo aziendale su misura e assistenza per tutta la durata della trattativa sindacale finalizzata alla sottoscrizione di accordi di secondo livello.",
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=900&q=80&auto=format",
    imageAlt: "Rapporti di lavoro — riunione sindacale",
    imageRight: false,
  },
  {
    id: "rappresentanza",
    label: "Rappresentanza",
    heading: "In nome e per conto di Confcommercio Roma rappresentiamo le aziende associate dinnanzi alle Istituzioni, portando avanti gli interessi dei settori e delle aziende rappresentate.",
    body: "Presidiamo contenuti e obiettivi di Leggi regionali, Accordi quadro e Documenti di programma.\n\nRappresentiamo gli interessi delle aziende nelle trattative nazionali di sottoscrizione e rinnovo dei principali CCNL di categoria, con particolare attenzione a: Terziario, Dirigenti Terziario, Turismo, Pubblici esercizi di cui la nostra Associazione è firmataria.\n\nDurante la vigenza dei CCNL l'area si occupa di monitorare l'applicazione contrattuale, proporre modifiche da attenzionare nel prossimo rinnovo e applicare, attraverso Commissioni tecniche, nuove norme previste dal CCNL.\n\nL'Area si occupa inoltre, del monitoraggio degli interessi aziendali per la sottoscrizione, con le Organizzazioni Sindacali, di accordi territoriali applicabili dalle aziende quali norme integrative al CCNL di categoria.\n\nDi particolare rilievo per il nostro territorio, sono i seguenti Accordi territoriali firmati ed immediatamente applicabili dalle aziende associate:",
    items: [
      "Accordo per la detassazione dei premi di produttività",
      "Accordo sulla stagionalità per l'inserimento di contratti a tempo determinato in specifici periodi dell'anno di maggior intensità lavorativa e nei territori definiti come stagionali dall'accordo",
    ],
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900&q=80&auto=format",
    imageAlt: "Rappresentanza — tavolo negoziale",
    imageRight: true,
  },
  {
    id: "informazione",
    label: "Informazione e Networking",
    heading: "Forniamo alle aziende un costante servizio di informazione circa le novità normative utili all'esercizio dell'attività di impresa.",
    body: "Il servizio si realizza attraverso una newsletter settimanale e l'organizzazione di eventi e webinar tematici in cui gli HR delle diverse aziende sono invitati a partecipare, favorendo per lo scambio di know how e best practice.",
    label2: "La newsletter settimanale include:",
    items: [
      "Novità, modifiche e innovazioni in ambito HR, giuslavoristico, fiscale e amministrativo",
      "Aggiornamenti su norme europee, nazionali e locali",
      "Indicazioni operative provenienti da enti quali INPS, INAIL e Ministero del Lavoro",
      "Monitoraggio delle pronunce della Corte di Cassazione e dei vari tribunali italiani o europei",
    ],
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=900&q=80&auto=format",
    imageAlt: "Networking — evento aziendale",
    imageRight: false,
  },
];

export default function ServiziLavoroPage() {
  return (
    <main id="main-content" data-testid="servizi-lavoro-page">

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative h-[300px] md:h-[360px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&q=60&auto=format"
          alt="Cosa offriamo"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.84)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <HeroBreadcrumb />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
            Area Lavoro e Welfare
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Cosa offriamo
          </h1>
          <p className="text-white/80 mt-3 max-w-2xl text-base leading-relaxed">
            Consulenza in diritto del lavoro, relazioni sindacali, gestione del contenzioso e sviluppo dei processi HR.
          </p>
        </div>
      </section>

      {/* ── Intro ─────────────────────────────────────────────────────── */}
      <section className="py-14 bg-white border-b border-[var(--confcom-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                Rappresentiamo le aziende associate nella trattativa e nella sottoscrizione dei principali{" "}
                <strong className="text-[var(--confcom-text-primary)]">Accordi interconfederali</strong>,{" "}
                <strong className="text-[var(--confcom-text-primary)]">Contratti Collettivi Nazionali di categoria</strong>{" "}
                e dei{" "}
                <strong className="text-[var(--confcom-text-primary)]">Contratti integrativi Territoriali e Aziendali</strong>.
              </p>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                Alle aziende associate e agli uffici del personale offriamo un servizio di{" "}
                <strong className="text-[var(--confcom-text-primary)]">consulenza</strong> in materia di{" "}
                <strong className="text-[var(--confcom-text-primary)]">diritto del lavoro</strong> e{" "}
                <strong className="text-[var(--confcom-text-primary)]">relazioni sindacali</strong>, gestione del{" "}
                <strong className="text-[var(--confcom-text-primary)]">contenzioso</strong> e sviluppo dei{" "}
                <strong className="text-[var(--confcom-text-primary)]">processi HR</strong>.
              </p>
              <div className="mt-8">
                <a
                  href="https://www.confcommercioroma.it/wp-content/uploads/2025/02/Scheda_Area-Lavoro-e-Welfare_26092024.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-7 py-3 rounded-md font-semibold text-sm hover:opacity-90 transition-opacity"
                >
                  <Download size={16} aria-hidden="true" /> Sfoglia la brochure
                </a>
              </div>
            </div>
            {/* Numeri chiave */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "+10.000", label: "Aziende assistite sul territorio" },
                { value: "+80.000", label: "Rapporti di lavoro gestiti" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="bg-[#EBF5FF] rounded-xl p-6 border border-[var(--confcom-border)] text-center"
                >
                  <div className="text-3xl font-bold text-[var(--confcom-blue)] font-['Encode_Sans_Semi_Condensed']">
                    {s.value}
                  </div>
                  <div className="text-xs text-[var(--confcom-text-secondary)] mt-1 leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Fasi del rapporto di lavoro ───────────────────────────────── */}
      <section className="py-16 bg-[var(--confcom-blue)]" id="fasi">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-white font-['Encode_Sans_Semi_Condensed']">
              Le fasi del rapporto di lavoro
            </h2>
          </div>

          {/* Le 3 fasi che compongono il rapporto di lavoro */}
          <div className="grid md:grid-cols-3 gap-4">
            {FASI.slice(0, 3).map((fase) => (
              <div
                key={fase.titolo}
                className="bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl p-5 transition-all group"
                data-testid={`fase-${fase.titolo.toLowerCase().replace(/\s/g, "-")}`}
              >
                {/* Numero + icona */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-4xl font-bold text-white/20 font-['Encode_Sans_Semi_Condensed'] leading-none select-none">
                    {fase.num}
                  </span>
                  <div className="w-10 h-10 bg-[#56B7E6]/20 rounded-lg flex items-center justify-center">
                    <fase.icon size={18} className="text-[#56B7E6]" aria-hidden="true" />
                  </div>
                </div>
                <h3 className="text-base font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-3">
                  {fase.titolo}
                </h3>
                <ul className="space-y-1.5">
                  {fase.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-white/70 leading-snug">
                      <span className="w-1 h-1 bg-[#56B7E6] rounded-full mt-1.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Separatore neutro verso le fasi eventuali (senza etichetta testuale) */}
          <div className="h-px bg-white/10 mt-10 mb-6" aria-hidden="true" />

          {/* Le 2 fasi eventuali (possibile piega negativa): stesso stile, dimensione ridotta */}
          <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto">
            {FASI.slice(3).map((fase) => (
              <div
                key={fase.titolo}
                className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 transition-all group"
                data-testid={`fase-${fase.titolo.toLowerCase().replace(/\s/g, "-")}`}
              >
                {/* Numero + icona */}
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-2xl font-bold text-white/15 font-['Encode_Sans_Semi_Condensed'] leading-none select-none">
                    {fase.num}
                  </span>
                  <div className="w-7 h-7 bg-[#56B7E6]/15 rounded-md flex items-center justify-center">
                    <fase.icon size={13} className="text-[#56B7E6]/80" aria-hidden="true" />
                  </div>
                </div>
                <h3 className="text-sm font-bold text-white/90 font-['Encode_Sans_Semi_Condensed'] mb-2">
                  {fase.titolo}
                </h3>
                <ul className="space-y-1">
                  {fase.items.map((item) => (
                    <li key={item} className="flex items-start gap-1.5 text-[0.7rem] text-white/50 leading-snug">
                      <span className="w-1 h-1 bg-[#56B7E6]/60 rounded-full mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4 aree di competenza — sezioni alternate ──────────────────── */}
      {SERVIZI.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`py-20 ${i % 2 === 0 ? "bg-white" : "bg-[#EBF5FF]"}`}
          data-testid={`servizio-${s.id}`}
        >
          {s.legacyId && <span id={s.legacyId} aria-hidden="true" />}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className={`grid lg:grid-cols-2 gap-14 items-center ${
                s.imageRight ? "" : "lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1"
              }`}
            >
              {/* Testo */}
              <div>
                {/* Label */}
                <div className="mb-5">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
                    {s.label}
                  </span>
                </div>

                {/* Heading */}
                <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug mb-5">
                  {s.heading}
                </h2>

                {/* Body */}
                {s.body.split("\n\n").map((para, pi) => (
                  <p key={pi} className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4 last:mb-8 text-[15px]">
                    {para}
                  </p>
                ))}

                {/* Bullet list */}
                <div>
                  {s.label2 && (
                    <p className="text-sm font-bold text-[var(--confcom-text-primary)] mb-4 uppercase tracking-wide font-['Encode_Sans_Semi_Condensed']">
                      {s.label2}
                    </p>
                  )}
                  <ul className="space-y-3">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle
                          size={17}
                          className="text-[var(--confcom-blue)] shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <span className="text-sm text-[var(--confcom-text-secondary)] leading-snug">
                          <strong className="text-[var(--confcom-text-primary)]">{item}</strong>
                        </span>
                      </li>
                    ))}
                  </ul>
                  {s.afterItems && (
                    <p className="text-[var(--confcom-text-secondary)] leading-relaxed mt-6 text-[15px]">
                      {s.afterItems}
                    </p>
                  )}
                </div>
              </div>

              {/* Immagine */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={s.image}
                  alt={s.imageAlt}
                  className="w-full h-80 lg:h-[420px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/cta-background.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.88)" }} />
        </div>
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per maggiori informazioni</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mt-3 mb-8">Contattaci</h2>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-left" data-testid="servizi-lavoro-contatti">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-white/80">
                <Mail size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="mailto:sindacale@confcommercioroma.it" className="hover:text-white transition-colors">sindacale@confcommercioroma.it</a>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Phone size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="tel:+390668437221" className="hover:text-white transition-colors">Tel: 06 68437 221-290</a>
              </div>
            </div>
          </div>

          <Link
            to="/chi-siamo/struttura-tecnica#ufficio-lavoro-e-welfare"
            data-testid="servizi-lavoro-scopri-team"
            className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-6 py-3 rounded-md font-semibold hover:bg-white/90 transition-colors text-sm mt-8"
          >
            Scopri il Team <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>

    </main>
  );
}
